import React, { useMemo, useState, useEffect, useRef } from "react";
import api from "../api";

// Widget flotante de WhatsApp inspirado en el diseño adjunto.
// Configuración vía variables Vite:
//   - VITE_WHATSAPP_PHONE: número con código de país sin + (ej: 593998235677)
//   - VITE_SITE_NAME: nombre a mostrar en el encabezado (ej: "Wave Sales")
//   - VITE_WHATSAPP_GREETING: mensaje inicial sugerido (opcional)

const FloatingWhatsApp = () => {
  const phone = import.meta.env.VITE_WHATSAPP_PHONE || ""; // ej: 593998235677
  const siteName = import.meta.env.VITE_SITE_NAME || "Wave Sales";
  const defaultGreeting = import.meta.env.VITE_WHATSAPP_GREETING || "Hola, tengo una consulta";
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "";
  const publicClientId = import.meta.env.VITE_WHATSAPP_PUBLIC_CLIENT_ID || "server-check";
  const greetingText = `Hola, somos el equipo de ${siteName}, por favor, escribe tu nombre y tu contacto de WhatsApp en la parte de arriba. ¿Cuéntanos, cómo te podemos ayudar?..`;
  const landingOriginPrefix = "[WaveSales Landing]";

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [visitorName, setVisitorName] = useState(() => localStorage.getItem('wa_visitor_name') || "");
  const [visitorPhone, setVisitorPhone] = useState(() => localStorage.getItem('wa_visitor_phone') || "");
  // Selector de país para prefijo telefónico (mostrar banderas y +código)
  const COUNTRY_OPTIONS = [
    { code: 'EC', name: 'Ecuador', dial: '593', flag: '🇪🇨' },
    { code: 'MX', name: 'México', dial: '52', flag: '🇲🇽' },
    { code: 'CO', name: 'Colombia', dial: '57', flag: '🇨🇴' },
    { code: 'PE', name: 'Perú', dial: '51', flag: '🇵🇪' },
    { code: 'CL', name: 'Chile', dial: '56', flag: '🇨🇱' },
    { code: 'AR', name: 'Argentina', dial: '54', flag: '🇦🇷' },
    { code: 'US', name: 'Estados Unidos', dial: '1', flag: '🇺🇸' },
    { code: 'ES', name: 'España', dial: '34', flag: '🇪🇸' },
  ];
  const [country, setCountry] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('wa_country') || 'null');
      if (saved && saved.code && saved.dial) return saved;
    } catch {}
    const p = String(import.meta.env.VITE_WHATSAPP_PHONE || '').replace(/\D/g,'');
    const found = COUNTRY_OPTIONS.find(c => p.startsWith(c.dial));
    return found || COUNTRY_OPTIONS[0];
  });
  const [countryOpen, setCountryOpen] = useState(false);
  const countryRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      try {
        if (!countryRef.current) return;
        if (!countryRef.current.contains(e.target)) setCountryOpen(false);
      } catch {}
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  const flagUrl = (code) => {
    try {
      const up = String(code || '').toUpperCase();
      const a = up.charCodeAt(0) - 65;
      const b = up.charCodeAt(1) - 65;
      const cp1 = (0x1F1E6 + a).toString(16);
      const cp2 = (0x1F1E6 + b).toString(16);
      return `https://twemoji.maxcdn.com/v/latest/svg/${cp1}-${cp2}.svg`;
    } catch { return ''; }
  };
  const [sending, setSending] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [messages, setMessages] = useState([
    { id: 'sys-greeting', role: 'agent', text: greetingText, time: new Date(), status: 'sent' },
  ]);
  const endRef = useRef(null);
  const recentLocalOutRef = useRef([]); // [{text, at:number}]
  // Audio sutil al recibir respuesta
  const audioCtxRef = useRef(null);
  const audioReadyRef = useRef(false);
  const ensureAudio = async () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      if (audioCtxRef.current.state === 'suspended') {
        try { await audioCtxRef.current.resume(); } catch {}
      }
      audioReadyRef.current = true;
    } catch {}
  };
  const playDing = () => {
    try {
      if (!audioReadyRef.current) return;
      const ctx = audioCtxRef.current; if (!ctx) return;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(880, ctx.currentTime);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.05, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      o.connect(g).connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.26);
    } catch {}
  };
  const [chatDigits, setChatDigits] = useState(() => {
    const fromStore = String((localStorage.getItem('wa_visitor_phone') || '').replace(/\D/g, ''));
    if (fromStore) return fromStore;
    return String((phone || '').replace(/\D/g, ''));
  });

  useEffect(() => {
    try { endRef.current?.scrollIntoView({ behavior: 'smooth' }); } catch {}
  }, [messages, open]);

  // Asegurar que el saludo esté presente al abrir el chat (sin duplicar)
  useEffect(() => {
    if (!open) return;
    setMessages((prev) => {
      if (prev.some((m) => m.id === 'sys-greeting')) return prev;
      return [{ id: 'sys-greeting', role: 'agent', text: greetingText, time: new Date(), status: 'sent' }, ...prev];
    });
  }, [open, greetingText]);

  // Suscribirse a respuestas reales del chat del número público (SSE vía proxy /wa para evitar mixed content/CORS)
  useEffect(() => {
    if (!open) return;
    let es = null;
    try {
  const digits = String(chatDigits || '').replace(/\D/g, '');
      if (!digits || !publicClientId) return;
      const chatId = `${digits}@c.us`;
      // Usar ruta relativa para aprovechar el proxy Nginx (/wa -> whatsapp-service)
      const url = `/wa/messages/stream/${encodeURIComponent(chatId)}?clientId=${encodeURIComponent(publicClientId)}`;
      es = new EventSource(url);
      try { console.debug('[WA] SSE connecting', { chatId, url }); } catch {}
      es.addEventListener('open', () => { try { console.debug('[WA] SSE open'); } catch {} });
      es.addEventListener('error', (ev) => { try { console.debug('[WA] SSE error', ev); } catch {} });
      es.addEventListener('message', (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (!data || !data.chatId) return;
          const wanted = chatId.toLowerCase();
          const rChat = String(data.chatId || '').toLowerCase();
          // Aceptar únicamente si el chatId coincide EXACTO (evita grupos y otros chats)
          const matches = (rChat === wanted);
          if (!matches) {
            try { console.debug('[WA] descartado por chatId', { rChat, wanted }); } catch {}
            return; // ignorar mensajes de otros grupos/chats
          }
          // Suprimir sólo eco del envío local reciente; permitir otros fromMe (operador)
          const isOut = (data.fromMe === true) || (String(data.direction || '').toLowerCase() === 'out');
          if (isOut) {
            const body = String(data.body || '').replace(/\r/g,'').trim();
            // Detectar el mensaje automático de recepción y usarlo para marcar doble palomita
            if (/hemos recibido tu mensaje/i.test(body)) {
              setMessages((prev) => {
                const copy = [...prev];
                for (let i = copy.length - 1; i >= 0; i--) {
                  if (copy[i].role === 'user' && copy[i].status !== 'error') {
                    copy[i] = { ...copy[i], status: 'delivered' };
                    break;
                  }
                }
                return copy;
              });
              return;
            }
            const now = Date.now();
            try {
              recentLocalOutRef.current = (recentLocalOutRef.current || []).filter(x => now - x.at < 15000);
              const duplicate = recentLocalOutRef.current.some(x => {
                if (x.text === body) return true;
                if (x.prefixed && (body === x.prefixed || body.startsWith(x.prefixed))) return true;
                if (body.startsWith(landingOriginPrefix)) {
                  const stripped = body.slice(landingOriginPrefix.length).replace(/^\n+/, '').trim();
                  return stripped === x.text;
                }
                return false;
              });
              if (duplicate) {
                try { console.debug('[WA] eco local suprimido'); } catch {}
                return; // ignorar eco de nuestro propio envío
              }
            } catch {}
          }
          const text = String(data.body || '').trim();
          if (!text) return;
          try { console.debug('[WA] inbound', { text }); } catch {}
          setMessages((prev) => {
            if (data.id && prev.some((m) => m.id === data.id)) return prev; // dedupe por id
            return [...prev, {
              id: data.id || `in-${Date.now()}`,
              role: 'agent',
              text,
              time: new Date(),
              status: 'sent',
            }];
          });
          // sonido sutil al recibir respuesta real del agente
          playDing();
        } catch (_) { /* ignorar */ }
      });
    } catch (_) { /* ignorar */ }
    return () => {
      try { es && es.close(); } catch (_) {}
    };
  }, [open, chatDigits, publicClientId]);

  // Ya no usamos el enlace a WhatsApp (solo envío directo)
  const waUrl = null;
  const canSend = true;

  // Cargar recaptcha v3 bajo demanda
  const ensureRecaptcha = () => new Promise((resolve, reject) => {
    // Evitar cargar reCAPTCHA cuando se navega por IP privada (muestra banner rojo de dominio no válido)
    const host = (typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : '';
    const isIP = /^\d{1,3}(?:\.\d{1,3}){3}$/.test(host);
    if (!recaptchaSiteKey || isIP) {
      // Resolver sin cargar script: el backend puede estar con bypass en desarrollo
      return resolve();
    }
    if (window.grecaptcha && window.grecaptcha.execute) return resolve();
    const existing = document.querySelector('script[data-rec="v3"]');
    if (existing) { existing.addEventListener('load', () => resolve()); return; }
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(recaptchaSiteKey)}`;
    s.async = true; s.defer = true; s.dataset.rec = 'v3';
    s.onload = () => resolve();
    s.onerror = () => resolve(); // no bloquear envío directo en dev
    document.head.appendChild(s);
  });

  const getRecaptchaToken = async (action = 'lead_message') => {
    await ensureRecaptcha();
    return new Promise((resolve, reject) => {
      try {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(recaptchaSiteKey, { action }).then(resolve).catch(reject);
        });
      } catch (e) { reject(e); }
    });
  };

  const sendDirect = async () => {
    if (!message.trim()) return alert('Escribe un mensaje');
    const text = message.trim();
    // Añadir el mensaje del usuario al hilo de forma optimista
    const tempId = `u-${Date.now()}`;
    setMessages((prev) => [...prev, { id: tempId, role: "user", text, time: new Date(), status: "pending" }]);
    setMessage("");
    // Registrar huella para suprimir eco del backend (que agrega un prefijo)
    try {
      const now = Date.now();
      recentLocalOutRef.current = (recentLocalOutRef.current || []).filter(x => now - x.at < 15000);
      const prefixed = `${landingOriginPrefix}\n${text}`;
      recentLocalOutRef.current.push({ text, prefixed, at: now });
    } catch {}
    setSending(true); setSentOk(false);
    try {
      let token = '';
      if (recaptchaSiteKey) {
        try { token = await getRecaptchaToken(); } catch (e) { token = ''; }
      }
  // Mostrar cero(s) inicial(es) si el usuario los escribe, pero internamente suprimirlos y anteponer prefijo del país
  const rawDigits = String(visitorPhone || '').replace(/\D/g, '');
  const localNoZero = rawDigits.replace(/^0+/, '');
  const intlDigits = (country?.dial || '') + localNoZero;
  const payload = { message: text, recaptcha: token };
  if (visitorName) payload.name = visitorName;
  if (intlDigits) payload.toPhone = intlDigits;
  const { data } = await api.post('public/lead-message/', payload);
      if (data && (data.status === 200 || String(data.status).startsWith('2'))) {
        setSentOk(true);
        // Marcar como enviado
        setMessages((prev) => prev.map((m) => m.id === tempId ? { ...m, status: 'sent' } : m));
        // Si el visitante proporcionó teléfono, seguir ese chat específico
        if (intlDigits) {
          try { localStorage.setItem('wa_visitor_phone', visitorPhone); } catch {}
          setChatDigits(intlDigits);
        }
        // Ya no enviamos respuesta automática en cada envío
      } else {
        // Marcar error en el mensaje
        setMessages((prev) => prev.map((m) => m.id === tempId ? { ...m, status: 'error' } : m));
        alert('No se pudo enviar. Intenta más tarde.');
      }
    } catch (e) {
      try {
        const status = e?.response?.status;
        const data = e?.response?.data;
        let msg = 'Error al enviar. ';
        if (status) msg += `(HTTP ${status}) `;
        if (data?.detail) msg += data.detail;
        if (data?.error === 'client_not_ready' || /client_not_ready/i.test(String(data?.error||''))) {
          msg += '\n\nAbre el QR y autentica la sesión de WhatsApp para el envío público.';
          msg += '\nSi estás en este equipo, visita: http://localhost:3001/qr-view?clientId=server-check';
        }
        alert(msg);
      } catch {
        alert('Error al enviar.');
      }
      // Marcar error en el mensaje
      setMessages((prev) => prev.map((m) => m.id === tempId ? { ...m, status: 'error' } : m));
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: "fixed", right: 16, bottom: 16, zIndex: 9999 }}>
      {/* Panel */}
      {open && (
        <div
          style={{
            width: 360,
            maxWidth: "calc(100vw - 24px)",
            background: "#ffffff",
            borderRadius: 12,
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            marginBottom: 12,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "#11866F", // verde-azulado
              color: "#fff",
              padding: "10px 12px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                background: "#0f7d66",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid rgba(255,255,255,0.85)",
              }}
            >
              {/* Bubble icon */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 12.5C20 17 15.97 20.5 11 20.5C9.68 20.5 8.43 20.25 7.31 19.8L4 21L4.86 17.83C3.7 16.66 3 15.13 3 13.5C3 9 7.03 5.5 12 5.5C16.97 5.5 21 9 21 13.5C21 13.84 20.98 14.18 20.94 14.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <strong style={{ lineHeight: 1 }}>{siteName}</strong>
              <span style={{ fontSize: 12, opacity: 0.9 }}>Escríbenos por WhatsApp</span>
            </div>
          </div>

          {/* Datos del visitante */}
          <div style={{ padding: 12, background: "#f1f5f9", borderBottom: "1px solid #e5e7eb", display: 'flex', gap: 8, flexWrap: 'nowrap', justifyContent: 'center', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Tu nombre"
              value={visitorName}
              onChange={(e) => { setVisitorName(e.target.value); try { localStorage.setItem('wa_visitor_name', e.target.value); } catch {} }}
              style={{ flex: '0 0 120px', width: 120, border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 13, color: '#111827' }}
            />
            {/* Campo combinado: bandera (selector) + prefijo + número en un solo recuadro */}
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                border: '1px solid #e5e7eb', borderRadius: 8,
                padding: '4px 8px', height: 36, background: '#fff',
                flex: '1 1 0', minWidth: 0
              }}
            >
              <div ref={countryRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}>
                <button type="button" onClick={() => setCountryOpen(o => !o)}
                  style={{ display:'flex', alignItems:'center', gap:6, background:'transparent', border:'none', padding:0, cursor:'pointer' }}
                  title={`Selecciona país (${country.name})`}>
                  <img src={flagUrl(country.code)} alt={country.name} width={20} height={14} style={{ display:'block' }} />
                  <span style={{ color: '#111827', fontSize: 14, opacity: 0.9 }}>+{country.dial}</span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 10l5 5 5-5" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {countryOpen && (
                  <div style={{ position:'absolute', top:'calc(100% + 6px)', left:'50%', transform:'translateX(-50%)', zIndex:10000, background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, boxShadow:'0 8px 24px rgba(0,0,0,.15)', maxHeight:260, overflowY:'auto', minWidth:280, maxWidth:320 }}>
                    {COUNTRY_OPTIONS.map((c) => (
                      <div key={c.code}
                        onMouseDown={(e)=>e.preventDefault()}
                        onClick={() => { setCountry(c); setCountryOpen(false); try{ localStorage.setItem('wa_country', JSON.stringify(c)); } catch{} }}
                        style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', cursor:'pointer', fontSize:14, color:'#111827', whiteSpace:'nowrap' }}
                      >
                        <img src={flagUrl(c.code)} alt={c.name} width={18} height={14} style={{ display:'block' }} />
                        <span style={{ flex:1 }}>{c.name}</span>
                        <span style={{ opacity: .8 }}>+{c.dial}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ width: 1, height: 20, background: '#e5e7eb', margin: '0 6px' }} />
              <input
                type="tel"
                placeholder={`0 998 335 577`}
                value={visitorPhone}
                onChange={(e) => { const v = e.target.value; setVisitorPhone(v); try { localStorage.setItem('wa_visitor_phone', v); } catch {} }}
                style={{
                  flex: 1, border: 'none', outline: 'none', background: 'transparent',
                  fontSize: 14, color: '#111827'
                }}
              />
            </div>
          </div>

          {/* Área de conversación */}
          <div style={{ padding: 12, background: "#f8fafc", display: 'flex', flexDirection: 'column' }}>
            <div style={{
              flex: 1,
              minHeight: 260,
              maxHeight: '60vh',
              overflowY: 'auto',
              paddingRight: 6,
            }}>
              {messages.map((m) => {
                const isUser = m.role === 'user';
                const bubbleStyle = isUser ? {
                  alignSelf: 'flex-end',
                  background: m.status === 'error' ? '#fee2e2' : '#dcfce7',
                  color: '#065f46',
                } : {
                  alignSelf: 'flex-start',
                  background: '#eef2f7',
                  color: '#334155',
                };
                return (
                  <div key={m.id} style={{ display: 'flex', marginBottom: 8, justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      ...bubbleStyle,
                      padding: '8px 10px',
                      borderRadius: 10,
                      boxShadow: 'inset 0 0 0 1px #e2e8f0',
                      fontSize: 14,
                      maxWidth: '85%',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                    }}>
                      {m.text}
                      {isUser && m.status === 'pending' && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#9ca3af' }}>✓</span>
                      )}
                      {isUser && m.status === 'sent' && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#94a3b8' }}>✓✓</span>
                      )}
                      {isUser && m.status === 'delivered' && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#0ea5e9' }}>✓✓</span>
                      )}
                      {isUser && m.status === 'error' && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#b91c1c' }}>Error</span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
          </div>

          {/* Input */}
          <div style={{ padding: 10, borderTop: "1px solid #e5e7eb", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {/* Estilos locales para placeholder */}
            <style>{`.fw-input::placeholder{color:#9ca3af}`}</style>
            <input
              type="text"
              placeholder="Pregúntame lo que quieras..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { sendDirect(); }
              }}
              style={{
                flex: 1,
                border: "1px solid #e5e7eb",
                borderRadius: 9999,
                padding: "10px 12px",
                outline: "none",
                fontSize: 14,
                background: "#ffffff",
                color: "#111827",
              }}
              className="fw-input"
            />
            {/* Enviar directo */}
            <button
              type="button"
              onClick={sendDirect}
              disabled={sending}
              title={"Enviar directo"}
              style={{
                padding: "10px 14px",
                minWidth: 140,
                borderRadius: 9999,
                background: sending ? "#94a3b8" : "#0ea5e9",
                color: "#fff",
                border: "none",
                cursor: sending ? "wait" : "pointer",
                fontSize: 14,
                height: 42,
              }}
            >{sending ? 'Enviando...' : (sentOk ? 'Enviado ✓' : 'Enviar directo')}</button>
          </div>
        </div>
      )}

      {/* Botón flotante */}
      <button
        type="button"
  onClick={async () => { await ensureAudio(); setOpen((o) => !o); }}
        aria-label="Abrir chat WhatsApp"
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "#11866F",
          color: "#fff",
          border: "none",
          boxShadow: "0 8px 24px rgba(0,0,0,.25)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 12.5C20 17 15.97 20.5 11 20.5C9.68 20.5 8.43 20.25 7.31 19.8L4 21L4.86 17.83C3.7 16.66 3 15.13 3 13.5C3 9 7.03 5.5 12 5.5C16.97 5.5 21 9 21 13.5C21 13.84 20.98 14.18 20.94 14.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
};

export default FloatingWhatsApp;
